/**
 * MOCK-ONLY page — preview of the proposed dark-mode UI.
 * Standalone, no API calls, hardcoded sample data.
 * Lives at /mock/dark — does NOT affect any real surfaces.
 * Delete this file once direction is approved or rejected.
 */
import React, { useState } from "react";
import MoonbucksLogo from "../components/MoonbucksLogo";
import { Bell, Coffee, Rocket, TrendingUp, Menu } from "lucide-react";

// Hardcoded design tokens (the proposed palette)
const PALETTE = {
  bgDeepSpace: "#0A0E14",
  surfaceEspresso: "#0F1A17",          // colder — more "instrument panel" feel
  surfaceEspressoElevated: "#13211C",
  border: "rgba(22, 213, 138, 0.12)",  // brand-tinted hairlines
  borderStrong: "rgba(22, 213, 138, 0.28)",
  parchment: "#E8F4ED",                // cooler tint — circuit-board ivory
  parchmentMuted: "rgba(232, 244, 237, 0.62)",
  parchmentSubtle: "rgba(232, 244, 237, 0.38)",
  emerald: "#16D58A",      // moonlight emerald (accent)
  emeraldDeep: "#043B2A",
  emeraldDim: "rgba(22, 213, 138, 0.55)",
  magenta: "#FF4D9D",      // brighter nebula magenta
  amber: "#F5A623",
  mars: "#FF5C5C",
};

const SAMPLE_ALERTS = [
  { sym: "AEO", cls: "steady",   status: "IN ZONE",  entry: 17.45, stop: 15.71, tp1: 18.50, moon: 20.94, now: 17.65, ret: 1.15, day: "Day 8/15" },
  { sym: "UPST", cls: "moonshot", status: "CHASING", entry: 31.09, stop: 27.98, tp1: 32.96, moon: 37.31, now: 33.40, ret: 7.43, day: "Day 6/25" },
  { sym: "BEAM", cls: "moonshot", status: "IN ZONE",  entry: 30.32, stop: 27.29, tp1: 32.14, moon: 36.38, now: 30.85, ret: 1.75, day: "Day 3/25" },
];

const SECTORS = [
  { label: "TECH",        regime: "bullish" },
  { label: "SEMIS",       regime: "bullish" },
  { label: "BIOTECH",     regime: "mixed"   },
  { label: "FINANCIALS",  regime: "bullish" },
  { label: "CONSUMER",    regime: "bullish" },
  { label: "ENERGY",      regime: "weak"    },
  { label: "INDUSTRIALS", regime: "bullish" },
  { label: "HEALTHCARE",  regime: "bullish" },
  { label: "SMALL CAPS",  regime: "bullish" },
];

const REGIME_BG = {
  bullish: PALETTE.emerald,
  mixed:   PALETTE.amber,
  weak:    PALETTE.mars,
};

export default function MockDark() {
  const [activeSector, setActiveSector] = useState(null);

  return (
    <div style={{ backgroundColor: PALETTE.bgDeepSpace, color: PALETTE.parchment, minHeight: "100vh", position: "relative", overflow: "hidden" }}>
      {/* COSMOS BACKDROP — layered emerald nebula gradients + static starfield, no animation */}
      <CosmosBackdrop />

      {/* Faint scanlines — futuristic CRT/HUD overlay, dialed way down */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 1,
        background: "repeating-linear-gradient(0deg, rgba(22, 213, 138, 0.04) 0px, rgba(22, 213, 138, 0.04) 1px, transparent 1px, transparent 3px)",
        mixBlendMode: "screen",
        opacity: 0.4,
      }} />

      {/* NAVBAR — dark espresso */}
      <header style={{
        position: "sticky", top: 0, zIndex: 50,
        backgroundColor: `${PALETTE.bgDeepSpace}e6`,
        backdropFilter: "blur(20px)",
        borderBottom: `1px solid ${PALETTE.border}`,
      }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full flex items-center justify-center"
                 style={{ backgroundColor: PALETTE.emerald, color: PALETTE.bgDeepSpace }}>
              <MoonbucksLogo className="w-5 h-5" />
            </div>
            <div style={{ fontFamily: "Newsreader, serif", fontWeight: 600, fontSize: "1.25rem", letterSpacing: "-0.01em" }}>
              Moonbucks
            </div>
            <span className="text-[10px] font-mono-data mt-1" style={{ color: PALETTE.parchmentSubtle, marginLeft: 4 }}>/ alerts</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: PALETTE.parchmentMuted }}>
            <span style={{ color: PALETTE.emerald, fontWeight: 500 }}>Dashboard</span>
            <span>Backtest</span>
            <span>Account</span>
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline font-mono-data text-xs" style={{ color: PALETTE.parchmentSubtle }}>demo@moonbucks.io</span>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 relative" style={{ zIndex: 2 }}>

        {/* HUD telemetry strip — mono terminal-style status row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 font-mono-data text-[11px] tracking-wider"
             style={{ color: PALETTE.parchmentSubtle }}>
          <span className="flex items-center gap-2">
            <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: PALETTE.emerald,
                           boxShadow: `0 0 8px ${PALETTE.emerald}`, animation: "pulse-dot 2s ease-in-out infinite" }} />
            <span style={{ color: PALETTE.emerald }}>SYS · ONLINE</span>
          </span>
          <span>SESSION 09:42:17 EST</span>
          <span>REGIME · BULLISH</span>
          <span>UNIVERSE · 297 SYMS</span>
          <span>NEXT SCAN · 12:00 EST</span>
        </div>

        <style>{`@keyframes pulse-dot {0%,100%{opacity:1;}50%{opacity:0.4;}}`}</style>

        {/* Greeting */}
        <div className="mb-8">
          <div className="label-uppercase" style={{ color: PALETTE.emerald, letterSpacing: "0.28em" }}>DASHBOARD</div>
          <h1 style={{ fontFamily: "Newsreader, serif", fontWeight: 500, fontSize: "2.75rem", letterSpacing: "-0.01em", marginTop: 6, color: PALETTE.parchment }}>
            Morning, Demo.
          </h1>
          <p className="text-sm mt-1" style={{ color: PALETTE.parchmentMuted }}>
            Live alerts from the analyst desk.
          </p>
        </div>

        {/* MARKET REGIME BANNER */}
        <div className="relative" style={{
          backgroundColor: PALETTE.surfaceEspresso,
          border: `1px solid ${PALETTE.border}`,
          borderRadius: "0.5rem",
          padding: "1.25rem 1.4rem",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 12,
          boxShadow: "0 24px 60px -28px rgba(22, 213, 138, 0.25), inset 0 1px 0 0 rgba(22, 213, 138, 0.08)",
        }}>
          <HudCorners color={PALETTE.emerald} />
          <div className="flex items-center gap-4">
            <span className="label-uppercase" style={{
              backgroundColor: PALETTE.emerald,
              color: PALETTE.bgDeepSpace,
              padding: "6px 12px",
              borderRadius: "0.375rem",
              boxShadow: `0 0 24px ${PALETTE.emerald}88, inset 0 1px 0 rgba(255,255,255,0.25)`,
              letterSpacing: "0.18em",
              fontWeight: 700,
            }}>
              ◉ MARKET · BULLISH
            </span>
            <span className="text-sm" style={{ color: PALETTE.parchmentMuted }}>
              The roast is dark. The setup is bullish.
            </span>
          </div>
          <div className="font-mono-data text-xs" style={{ color: PALETTE.parchmentSubtle, letterSpacing: "0.05em" }}>
            SPY <span style={{ color: PALETTE.parchment }}>$737.05</span>
            <span className="mx-2">·</span>
            50-SMA <span style={{ color: PALETTE.parchment }}>$717.45</span>
            <span className="mx-2">·</span>
            <span style={{ color: PALETTE.emerald }}>+2.73%</span>
          </div>
        </div>

        {/* SECTOR PILLS */}
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {SECTORS.map(s => {
            const isActive = activeSector === s.label;
            return (
              <button
                key={s.label}
                onClick={() => setActiveSector(isActive ? null : s.label)}
                className="font-mono-data text-[10px] tracking-wider font-bold transition-all"
                style={{
                  backgroundColor: REGIME_BG[s.regime],
                  color: PALETTE.bgDeepSpace,
                  padding: "10px 12px",
                  borderRadius: "0.375rem",
                  border: "none",
                  cursor: "pointer",
                  letterSpacing: "0.16em",
                  boxShadow: isActive
                    ? `0 0 0 1px ${PALETTE.parchment}, 0 0 20px ${REGIME_BG[s.regime]}aa, inset 0 1px 0 rgba(255,255,255,0.25)`
                    : `0 0 12px ${REGIME_BG[s.regime]}44, inset 0 1px 0 rgba(255,255,255,0.18)`,
                  transform: isActive ? "translateY(-2px)" : "none",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {/* TWO COLUMN: Alert feed + Strategy CTA */}
        <div className="grid lg:grid-cols-12 gap-6 mt-6">
          {/* ALERT FEED */}
          <section className="lg:col-span-8 relative" style={{
            backgroundColor: PALETTE.surfaceEspresso,
            border: `1px solid ${PALETTE.border}`,
            borderRadius: "0.5rem",
            padding: "1.5rem",
            boxShadow: "0 32px 80px -36px rgba(22, 213, 138, 0.25), inset 0 1px 0 0 rgba(22, 213, 138, 0.08)",
          }}>
            <HudCorners color={PALETTE.emerald} />
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-uppercase" style={{ color: PALETTE.parchmentMuted, letterSpacing: "0.28em" }}>LIVE ALERT FEED</div>
                <h2 style={{ fontFamily: "Newsreader, serif", fontWeight: 600, fontSize: "1.65rem", marginTop: 6 }}>Published alerts</h2>
              </div>
              <div className="flex items-center gap-2 font-mono-data text-[11px]" style={{ color: PALETTE.emerald }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: PALETTE.emerald,
                               boxShadow: `0 0 8px ${PALETTE.emerald}`, animation: "pulse-dot 2s ease-in-out infinite" }} />
                3 ACTIVE
              </div>
            </div>

            <ul className="space-y-3">
              {SAMPLE_ALERTS.map(a => <DarkAlertRow key={a.sym} a={a} />)}
            </ul>
          </section>

          {/* STRATEGY SIDEBAR */}
          <aside className="lg:col-span-4">
            <div className="relative" style={{
              backgroundColor: PALETTE.surfaceEspressoElevated,
              border: `1px solid ${PALETTE.border}`,
              borderRadius: "0.5rem",
              padding: "1.5rem",
              boxShadow: "0 20px 50px -28px rgba(22, 213, 138, 0.20), inset 0 1px 0 0 rgba(22, 213, 138, 0.08)",
            }}>
              <HudCorners color={PALETTE.emeraldDim} />
              <div className="label-uppercase" style={{ color: PALETTE.parchmentMuted, letterSpacing: "0.28em" }}>STRATEGY</div>
              <h3 style={{ fontFamily: "Newsreader, serif", fontWeight: 600, fontSize: "1.35rem", marginTop: 6 }}>Test the edge</h3>
              <p className="text-sm mt-2" style={{ color: PALETTE.parchmentMuted }}>
                12-month backtest of the Moonbucks strategy on 20 leaders.
              </p>
              <button style={{
                marginTop: 18,
                backgroundColor: "transparent",
                color: PALETTE.parchment,
                border: `1px solid ${PALETTE.borderStrong}`,
                padding: "9px 18px",
                borderRadius: "0.375rem",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: "0.825rem",
                fontFamily: "JetBrains Mono, monospace",
                letterSpacing: "0.05em",
              }}>
                RUN BACKTEST →
              </button>
            </div>

            {/* Desk sentiment — HUD instrument panel */}
            <div className="mt-6 relative" style={{
              padding: "1.5rem",
              backgroundColor: PALETTE.surfaceEspresso,
              border: `1px solid ${PALETTE.border}`,
              borderRadius: "0.5rem",
              boxShadow: "0 20px 50px -28px rgba(22, 213, 138, 0.20), inset 0 1px 0 0 rgba(22, 213, 138, 0.08)",
            }}>
              <HudCorners color={PALETTE.emeraldDim} />
              <div className="flex items-center justify-between">
                <div className="label-uppercase" style={{ color: PALETTE.emerald, letterSpacing: "0.28em" }}>
                  // DESK CONVICTION
                </div>
                <span className="font-mono-data text-[10px]" style={{ color: PALETTE.parchmentSubtle }}>09:42 EST</span>
              </div>
              <div className="font-display mt-3 flex items-baseline gap-3" style={{ fontFamily: "Newsreader, serif" }}>
                <span style={{ fontSize: "2.5rem", fontWeight: 500, color: PALETTE.emerald, lineHeight: 1, letterSpacing: "-0.02em",
                              textShadow: `0 0 22px ${PALETTE.emerald}55` }}>
                  09
                </span>
                <span className="font-mono-data text-base" style={{ color: PALETTE.parchmentMuted }}>/ 10</span>
                <span className="font-mono-data text-[11px] tracking-wider"
                      style={{ marginLeft: 8, padding: "3px 8px", borderRadius: 4,
                               backgroundColor: `${PALETTE.emerald}22`, color: PALETTE.emerald,
                               border: `1px solid ${PALETTE.emerald}55`, letterSpacing: "0.15em" }}>
                  DOUBLE SHOT
                </span>
              </div>
              <p className="text-xs mt-3" style={{ color: PALETTE.parchmentMuted, lineHeight: 1.6 }}>
                3 active Moonshot alerts in confirmed bull regime. Conviction high.
              </p>
              {/* Mini bar gauge — instrument visual */}
              <div className="mt-4 flex gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <span key={i} style={{
                    flex: 1, height: 4, borderRadius: 1,
                    backgroundColor: i < 9 ? PALETTE.emerald : "rgba(22, 213, 138, 0.18)",
                    boxShadow: i < 9 ? `0 0 6px ${PALETTE.emerald}88` : "none",
                  }} />
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* CTA — primary emerald with glow */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button style={{
            backgroundColor: PALETTE.emerald,
            color: PALETTE.bgDeepSpace,
            padding: "12px 28px",
            borderRadius: "0.375rem",
            border: "none",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: "0.85rem",
            letterSpacing: "0.12em",
            fontFamily: "JetBrains Mono, monospace",
            boxShadow: `0 0 28px ${PALETTE.emerald}66, inset 0 1px 0 rgba(255,255,255,0.25)`,
            transition: "all 0.2s",
          }}>
            SKIP THE LATTE → CATCH THE ROCKET
          </button>
          <button style={{
            backgroundColor: "transparent",
            color: PALETTE.parchment,
            border: `1px solid ${PALETTE.borderStrong}`,
            padding: "12px 24px",
            borderRadius: "0.375rem",
            cursor: "pointer",
            fontWeight: 500,
            fontSize: "0.82rem",
            letterSpacing: "0.08em",
            fontFamily: "JetBrains Mono, monospace",
          }}>
            VIEW TRACK RECORD →
          </button>
        </div>

        {/* Footer note */}
        <div className="mt-12 pt-6 text-xs font-mono-data" style={{
          borderTop: `1px solid ${PALETTE.border}`,
          color: PALETTE.parchmentSubtle,
          letterSpacing: "0.1em",
        }}>
          // MOCK PREVIEW · DARK MODE DIRECTION · No live data · Not yet implemented
        </div>
      </div>
    </div>
  );
}

/**
 * CosmosBackdrop — galaxy black-and-green ambient backdrop.
 * Layered radial gradients (emerald nebulae) + a sparse static starfield.
 * Subtle "mood lighting" — never demanding attention, never animated.
 */
function CosmosBackdrop() {
  // Static starfield generated once. Mix of small/medium/bright stars + faint green dust.
  const stars = React.useMemo(() => {
    const rng = (seed) => {
      let x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    return Array.from({ length: 280 }, (_, i) => {
      const sizeRoll = rng(i * 4.7);
      // Mostly small (sub-pixel), a few medium, very rare bright
      let size, opacity, glow, color;
      if (sizeRoll > 0.97) {
        // Bright "anchor" star — has noticeable glow
        size = 2.4 + rng(i * 9.1) * 1.2;
        opacity = 0.85 + rng(i * 11.3) * 0.15;
        glow = true;
      } else if (sizeRoll > 0.78) {
        // Medium
        size = 1.4 + rng(i * 6.3) * 0.8;
        opacity = 0.5 + rng(i * 3.7) * 0.35;
        glow = false;
      } else {
        // Small (the field)
        size = 0.7 + rng(i * 8.9) * 0.6;
        opacity = 0.25 + rng(i * 5.1) * 0.4;
        glow = false;
      }
      // ~12% of stars get a faint emerald tint
      color = rng(i * 17.7) > 0.88 ? "rgba(140, 235, 195, 1)" : "#EFEAD8";
      return {
        top: rng(i * 7.3) * 100,
        left: rng(i * 13.1) * 100,
        size,
        opacity,
        glow,
        color,
      };
    });
  }, []);

  return (
    <>
      {/* Nebula gradients — multiple emerald soft glows + violet hint + galaxy dust band */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        background: `
          radial-gradient(ellipse 1100px 700px at 15% -10%, rgba(22, 213, 138, 0.20), transparent 60%),
          radial-gradient(ellipse 900px 600px at 88% 25%, rgba(10, 95, 70, 0.26), transparent 65%),
          radial-gradient(ellipse 800px 500px at 50% 110%, rgba(22, 213, 138, 0.12), transparent 55%),
          radial-gradient(ellipse 600px 400px at 92% 95%, rgba(64, 32, 96, 0.16), transparent 60%),
          radial-gradient(ellipse 1400px 200px at 60% 65%, rgba(22, 213, 138, 0.08), transparent 70%)
        `,
      }} />
      {/* Galaxy dust band — faint diagonal smear (Milky Way style) */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        background: "linear-gradient(105deg, transparent 30%, rgba(22, 213, 138, 0.04) 50%, transparent 70%)",
      }} />
      {/* Soft vignette — pulls eyes to center */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        background: "radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.55) 100%)",
      }} />
      {/* Starfield — 280 stars varied by size, brightness, color */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        {stars.map((s, i) => (
          <span key={i} style={{
            position: "absolute",
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            backgroundColor: s.color,
            borderRadius: "50%",
            opacity: s.opacity,
            boxShadow: s.glow ? `0 0 6px ${s.color}, 0 0 10px rgba(22, 213, 138, 0.4)` : "none",
          }} />
        ))}
      </div>
    </>
  );
}

/**
 * HudCorners — 4 small L-shaped tick marks in the corners of a card,
 * giving it that viewfinder / instrument-panel feel. Position-absolute.
 */
function HudCorners({ color = "rgba(22, 213, 138, 0.6)" }) {
  const len = 12;       // arm length
  const off = -1;       // sit on the border, not inside
  const thick = 1;      // 1px tick
  const base = {
    position: "absolute",
    pointerEvents: "none",
  };
  return (
    <>
      {/* TL */}
      <span style={{ ...base, top: off, left: off, width: len, height: thick, backgroundColor: color }} />
      <span style={{ ...base, top: off, left: off, width: thick, height: len, backgroundColor: color }} />
      {/* TR */}
      <span style={{ ...base, top: off, right: off, width: len, height: thick, backgroundColor: color }} />
      <span style={{ ...base, top: off, right: off, width: thick, height: len, backgroundColor: color }} />
      {/* BL */}
      <span style={{ ...base, bottom: off, left: off, width: len, height: thick, backgroundColor: color }} />
      <span style={{ ...base, bottom: off, left: off, width: thick, height: len, backgroundColor: color }} />
      {/* BR */}
      <span style={{ ...base, bottom: off, right: off, width: len, height: thick, backgroundColor: color }} />
      <span style={{ ...base, bottom: off, right: off, width: thick, height: len, backgroundColor: color }} />
    </>
  );
}

function DarkAlertRow({ a }) {
  const isMoon = a.cls === "moonshot";
  const accent = isMoon ? PALETTE.magenta : PALETTE.emerald;
  const range = a.moon - a.stop;
  const nowPct = ((a.now - a.stop) / range) * 100;
  const entryPct = ((a.entry - a.stop) / range) * 100;
  const tp1Pct = ((a.tp1 - a.stop) / range) * 100;

  return (
    <li className="relative" style={{
      backgroundColor: PALETTE.surfaceEspressoElevated,
      border: `1px solid ${isMoon ? "rgba(255, 77, 157, 0.30)" : PALETTE.border}`,
      borderRadius: "0.5rem",
      padding: "1rem 1.1rem",
      transition: "all 0.2s",
      boxShadow: isMoon
        ? "0 18px 40px -28px rgba(255, 77, 157, 0.45), inset 0 1px 0 0 rgba(255, 77, 157, 0.10)"
        : "0 16px 36px -28px rgba(22, 213, 138, 0.35), inset 0 1px 0 0 rgba(22, 213, 138, 0.10)",
    }}>
      <HudCorners color={isMoon ? "rgba(255, 77, 157, 0.55)" : PALETTE.emeraldDim} />
      {/* Header: dot + symbol + status | pnl */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            backgroundColor: accent,
            boxShadow: `0 0 12px ${accent}, 0 0 2px ${accent}`,
            animation: a.status === "IN ZONE" ? "pulse-dot 2.4s ease-in-out infinite" : "none",
          }} />
          <span className="font-mono-data" style={{ fontWeight: 700, fontSize: "1.25rem", color: PALETTE.parchment, letterSpacing: "0.04em" }}>
            {a.sym}
          </span>
          <span className="label-uppercase text-[10px]" style={{
            backgroundColor: `${accent}15`,
            color: accent,
            border: `1px solid ${accent}55`,
            padding: "3px 10px",
            borderRadius: "0.25rem",
            letterSpacing: "0.18em",
            fontWeight: 700,
          }}>
            {isMoon ? "▲ " : "● "}{a.status}
          </span>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono-data font-bold" style={{
            fontSize: "1.15rem",
            color: a.ret >= 0 ? PALETTE.emerald : PALETTE.mars,
            textShadow: `0 0 10px ${a.ret >= 0 ? PALETTE.emerald : PALETTE.mars}55`,
            letterSpacing: "0.02em",
          }}>
            {a.ret >= 0 ? "+" : ""}{a.ret}%
          </div>
          <div className="font-mono-data text-[10px] mt-0.5" style={{ color: PALETTE.parchmentSubtle, letterSpacing: "0.1em" }}>
            {a.day.toUpperCase()} · JUN 9
          </div>
        </div>
      </div>

      {/* PriceContinuum — same structure, dark + glow */}
      <div className="mt-4">
        <div style={{
          position: "relative", height: 6, borderRadius: 9999, overflow: "visible",
          background: `linear-gradient(to right, ${PALETTE.mars}44, ${PALETTE.surfaceEspressoElevated} 50%, ${PALETTE.emerald}44)`,
          border: `1px solid ${PALETTE.border}`,
        }}>
          {/* Entry tick */}
          <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)",
                        left: `${entryPct}%`, width: 2, height: 14, backgroundColor: PALETTE.parchment, opacity: 0.8 }} />
          {/* TP1 tick */}
          <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)",
                        left: `${tp1Pct}%`, width: 2, height: 14, backgroundColor: PALETTE.emerald,
                        boxShadow: `0 0 6px ${PALETTE.emerald}`, opacity: 0.95 }} />
          {/* Now dot — comet style with glow */}
          <div style={{
            position: "absolute", top: -3,
            left: `calc(${nowPct}% - 6px)`,
            width: 12, height: 12, borderRadius: "50%",
            backgroundColor: a.ret >= 0 ? PALETTE.emerald : PALETTE.mars,
            border: `2px solid ${PALETTE.parchment}`,
            boxShadow: `0 0 14px ${a.ret >= 0 ? PALETTE.emerald : PALETTE.mars}, -22px 0 16px -8px ${a.ret >= 0 ? PALETTE.emerald : PALETTE.mars}77`,
          }} />
        </div>
        <div className="mt-2 flex justify-between font-mono-data text-[10px]" style={{ color: PALETTE.parchmentSubtle, letterSpacing: "0.05em" }}>
          <span>STOP ${a.stop}</span>
          <span style={{ color: PALETTE.parchment, fontWeight: 600 }}>ENTRY ${a.entry}</span>
          <span style={{ color: PALETTE.emerald }}>TP1 ${a.tp1}</span>
          <span style={{ color: PALETTE.amber }}>MOON ${a.moon}</span>
        </div>
      </div>
    </li>
  );
}
